import classNames from 'classnames';
import React, { useRef, useEffect, forwardRef, useId, memo } from 'react';
import classes from './RadioGroup.module.scss';

export interface RadioProps {
    buttonRef: React.RefObject<HTMLDivElement>;
    value: string;
    label: string;
    checked: boolean;
    setValue?: (value: string) => void;
    width: string;
    name?: string;
    index: number;
}

const defaultRadioProps = {
    setValue: () => null,
    name: '',
};

export interface RadioSelectProps {
    options: { label: string; value: string }[];
    value?: string;
    onChange?: (value: string) => void;
    label?: string | null;
    name?: string;
    isSmall?: boolean;
}

const defaultRadioSelectProps = {
    value: '',
    onChange: () => null,
    label: '',
    name: '',
    isSmall: false,
};

const Radio = forwardRef<HTMLInputElement, RadioProps>(
    ({ value, label, buttonRef, checked, setValue, width, name, index }, ref) => {
        const labelRef = useRef<HTMLLabelElement>(null);

        useEffect(() => {
            if (!checked) return;
            const button = buttonRef.current;
            const labelEl = labelRef.current;
            if (!button || !labelEl) return;
            button.style.left = `${Number(width.slice(0, -1)) * index}%`;
        }, [checked, buttonRef, width, index]);

        return (
            <>
                <input
                    className={classes['radio-input']}
                    type="radio"
                    id={value}
                    value={value}
                    checked={checked}
                    onChange={() => setValue?.(value)}
                    name={name}
                    ref={ref}
                />
                <label
                    className={classes['radio-label']}
                    ref={labelRef}
                    htmlFor={value}
                    style={{ width }}
                >
                    {label}
                </label>
            </>
        );
    }
);

Radio.defaultProps = defaultRadioProps;

const RadioGroup = forwardRef<HTMLInputElement, RadioSelectProps>(
    ({ options, value = options[0].value, onChange, label = '', name, isSmall }, ref) => {
        const buttonRef = useRef<HTMLDivElement>(null);
        const groupLabelId = useId();

        return (
            <div
                aria-labelledby={groupLabelId}
                className={classNames(classes.container, { [classes.small]: isSmall })}
            >
                {label && (
                    <div id={groupLabelId} className={classes.label}>
                        {label}
                    </div>
                )}
                <div className={classes.group}>
                    {options.map((option, i) => (
                        <Radio
                            index={i}
                            key={option.value}
                            value={option.value}
                            label={option.label}
                            buttonRef={buttonRef}
                            checked={option.value === value}
                            setValue={onChange}
                            name={name}
                            width={`${100 / options.length}%`}
                            ref={ref}
                        />
                    ))}
                    <div
                        className={classes['radio-range']}
                        ref={buttonRef}
                        style={{ width: `${100 / options.length}%` }}
                    />
                </div>
            </div>
        );
    }
);
RadioGroup.defaultProps = defaultRadioSelectProps;

export default memo(RadioGroup);
